import { ChangeDetectorRef, Component, OnInit, Inject, ViewChild } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import { ARService } from '../../ar/ar.service';
import { CaseFormsService } from '../case-forms.service';
import { TdoTableV2Component } from '../../../tdo-controls/common/tdo-table-v2/tdo-table-v2.component';
import { TdoTableFooterDataModel } from '../../../tdo-controls/common/tdo-table-v2/models/tdo-table-footer-data.model';
import { DataARLedgerListModel } from '../../ar/models/data-ar-ledger-list.model';
import { cfgCaseProcedure } from '../../ar/_models/ar.models';
import { TdoTableSelectionEvent } from '../../../tdo-controls/common/tdo-table-v2/models/tdo-table-selection.event';
import { Globals } from '../../../globals';
import { MessagingService } from '../../../services/messaging.service';
import { MessagingConstants } from '../../../models/MessagingConstants';
import { Constants } from '../../../constants';
import { ProceduresService } from '../../configuration/procedures/procedures.service';
import { CaseCreateWizardService } from '../../case-create-wizard/case-create-wizard.service';
import { CaseProcedureConfigurationModel } from '../../../models/caseProcedures/case-procedure-configuration.model';

@Component({
  standalone: false,
  selector: 'app-case-add-procedure',
  templateUrl: './case-add-procedure.component.html',
  styleUrls: ['./case-add-procedure.component.css']
})
export class CaseAddProcedureComponent implements OnInit {

  @ViewChild(TdoTableV2Component) tdoTable: TdoTableV2Component;
  footerData: TdoTableFooterDataModel[] = [];
  searchPredicate: (data: any, filter: string) => boolean;

  PatientCaseId: string;
  isLoading: boolean = false;
  cfgCaseProcedures: cfgCaseProcedure[] = [];
  selectedCaseProcedure: cfgCaseProcedure[] = [];
  isNewCase: boolean = false;
  isSingleSelection: boolean = false;

  // Sub-procedure mode (Case View "Add Sub-Procedure"): user first picks a
  // top-level parent procedure, then selects one of its sub-procedures.
  isSubProcedureMode: boolean = false;
  isLoadingSubProcedures: boolean = false;
  parentProcedures: cfgCaseProcedure[] = [];
  selectedParentProcedure: cfgCaseProcedure | null = null;
  subProcedures: cfgCaseProcedure[] = [];

  // Sub-procedures are fetched per parent and cached by CaseProcedureId, so
  // re-picking a parent does not hit the API again.
  private readonly subProcedureCache = new Map<string, cfgCaseProcedure[]>();

  constructor(
    public dialogRef: MatDialogRef<CaseAddProcedureComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly arService: ARService,
    private readonly snackBar: MatSnackBar,
    private readonly caseService: CaseFormsService,
    private readonly messagingService: MessagingService,
    public readonly globals: Globals,
    private readonly proceduresService: ProceduresService,
    private readonly cdr: ChangeDetectorRef,
    private readonly caseCreateWizardService: CaseCreateWizardService,
  ) {
    this.searchPredicate = (dataSearch: cfgCaseProcedure, filter: string) => {
      const dataCode: string = dataSearch.Code ? dataSearch.Code : '';
      const dataDesc: string = dataSearch.ProcedureName ? dataSearch.ProcedureName : '';
      return dataCode.toLowerCase().includes(filter) || dataDesc.toLowerCase().includes(filter);
    };
  }

  ngOnInit() {
    this.PatientCaseId = this.data?.PatientCaseId;
    this.isNewCase = !!this.data?.isNewCase;
    this.isSingleSelection = !!this.data?.isSingleSelection;

    const isAddCodeNotFound = !!this.data?.isAddCodeNotFound;
    // Only the Case View "Add Sub-Procedure" path uses the two-step
    // parent -> sub-procedure selection. The add-code-not-found and new-case
    // paths keep the original flat top-level procedure picker.
    this.isSubProcedureMode = !isAddCodeNotFound && !this.isNewCase;
    this.init();
  }

  async init() {
    this.isLoading = true;
    try {
      const results = await this.getCfgCaseProcedures();
      const procedures = Array.isArray(results) ? results : [];
      const topLevel = procedures.filter(
        p => p.ParentCaseProcedureId === null || p.ParentCaseProcedureId === Constants.emptyGuid
      );
      if (this.isSubProcedureMode) {
        // Populate the parent dropdown; the table stays empty until a parent
        // is chosen, so a top-level procedure cannot be added directly. The
        // sub-procedures themselves are not in this response -- getCfgCaseProcedures
        // only returns procedures in the appointment group, which children never
        // belong to -- so they are fetched per parent in onParentSelected().
        this.parentProcedures = topLevel;
        this.cfgCaseProcedures = [];
      } else {
        this.cfgCaseProcedures = topLevel;
      }
    } catch (err) {
      console.error('Error loading procedures:', err);
      this.createMessage('Something went wrong', 'Error');
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  getCfgCaseProcedures() {
    return firstValueFrom(this.arService.getCfgCaseProcedures(this.globals.LOCID));
  }

  async onParentSelected(parent: cfgCaseProcedure | null) {
    this.selectedParentProcedure = parent;
    this.clearSubProcedureSelection();

    if (!parent) {
      this.isLoadingSubProcedures = false;
      this.cdr.detectChanges();
      return;
    }

    const parentId = parent.CaseProcedureId;

    const cached = this.subProcedureCache.get(parentId);
    if (cached) {
      this.applySubProcedures(cached);
      this.isLoadingSubProcedures = false;
      this.cdr.detectChanges();
      return;
    }

    this.isLoadingSubProcedures = true;
    this.cdr.detectChanges();

    try {
      const children = await firstValueFrom(this.caseCreateWizardService.checkIfParentProcedure(parentId));

      // The user may have switched parents while this request was in flight.
      if (this.selectedParentProcedure?.CaseProcedureId !== parentId) {
        return;
      }

      const mapped = (Array.isArray(children) ? children : []).map(c => this.toCaseProcedure(c));
      this.subProcedureCache.set(parentId, mapped);
      this.applySubProcedures(mapped);
    } catch (err) {
      console.error('Error loading sub-procedures:', err);
      if (this.selectedParentProcedure?.CaseProcedureId === parentId) {
        this.createMessage('Something went wrong', 'Error');
      }
    } finally {
      if (this.selectedParentProcedure?.CaseProcedureId === parentId) {
        this.isLoadingSubProcedures = false;
      }
      this.cdr.detectChanges();
    }
  }

  get hasSelectedParent(): boolean {
    return !!this.selectedParentProcedure;
  }

  get isSubProcedureListEmpty(): boolean {
    return this.hasSelectedParent && !this.isLoadingSubProcedures && this.cfgCaseProcedures.length === 0;
  }

  private clearSubProcedureSelection() {
    // Untick the table while the outgoing rows are still bound, then empty it.
    this.tdoTable?.deselectAll();
    this.selectedCaseProcedure = [];
    this.subProcedures.forEach(p => p.DATA_SELECTED = false);
    this.subProcedures = [];
    this.cfgCaseProcedures = [];
  }

  /**
   * Show a parent's sub-procedures. Cached rows are the same object references
   * the user may have ticked earlier, so their selection flag is cleared here to
   * keep the table in step with the (already reset) selectedCaseProcedure list.
   */
  private applySubProcedures(subProcedures: cfgCaseProcedure[]) {
    subProcedures.forEach(p => p.DATA_SELECTED = false);
    this.subProcedures = subProcedures;
    this.cfgCaseProcedures = subProcedures;
  }

  /**
   * The sub-procedure endpoint returns configuration models; map them to the
   * shape the table and the save payload use. Mapping rather than casting also
   * guarantees a cached row never carries a stale DATA_SELECTED tick.
   */
  private toCaseProcedure(child: CaseProcedureConfigurationModel): cfgCaseProcedure {
    return {
      ...(child as unknown as cfgCaseProcedure),
      CaseProcedureId: child.CaseProcedureId,
      ProcedureName: child.ProcedureName,
      Code: child.Code,
      PresentOrder: child.PresentOrder,
      ParentCaseProcedureId: child.ParentCaseProcedureId,
      DATA_SELECTED: false
    };
  }

  createProcedure(procedures: cfgCaseProcedure[]) {

    // addProcedureForExistingCase
    // const CaseProcedureId = procedure.CaseProcedureId;
    const CaseProcedures = procedures;
    const PatientCaseId = this.PatientCaseId;

    this.isLoading = true;
    this.caseService.addProcedureForExistingCaseCaseView(PatientCaseId, CaseProcedures)
      .subscribe({
        next: (response) => {
          this.createMessage('Procedure added to the case', 'Success');
          this.isLoading = false;
          this.cancel(true);
          if (!PatientCaseId) {
            this.messagingService.sendComponentMessage(MessagingConstants.CASES_REFRESH, response);
          }
        },
        error: (err) => {
          this.createMessage('Something went wrong', 'Error');
          this.isLoading = false;
        }
      });
  }

  createMessage(message, type) {
    this.snackBar.open(message, type, {
      duration: 3000,
      verticalPosition: 'top'
    });
  }

  onRowSelected(event: TdoTableSelectionEvent) {
    const procedure = this.cfgCaseProcedures.find(p => p === event.model);
    // this.selectedCaseProcedure = [];
    if (procedure) {
      procedure.DATA_SELECTED = event.isSelected;

      if (event.isSelected) {
        if (!this.selectedCaseProcedure.includes(procedure)) {
          this.selectedCaseProcedure.push(procedure);
        }
      } else {
        const idx = this.selectedCaseProcedure.findIndex(p => p === procedure);
        if (idx > -1) {
          this.selectedCaseProcedure.splice(idx, 1);
        }
      }
    }
  }

  cancel(payload) {
    this.dialogRef.close(payload);
  }

  saveProcedure() {
    if (this.selectedCaseProcedure) {
      if (this.data.isAddCodeNotFound) {
        this.addProcedureCode(this.data.searchCode, this.data.doctorId, this.data.patientId, this.selectedCaseProcedure);
      }
      else if (!this.isNewCase) {
        this.createProcedure(this.selectedCaseProcedure);
      }
      else {
        this.dialogRef.close(this.selectedCaseProcedure[0]);
      }
    }
  }
  addProcedureCode(searchCode: string, doctorId: string, patientId: string, selectedCaseProcedure: cfgCaseProcedure[]) {
    this.isLoading = true;
    this.proceduresService.createProcedureCode(searchCode, doctorId, patientId, selectedCaseProcedure[0]).subscribe({
      next: (result) => {
        if (result) {
          this.createMessage('Procedure Code added successfully', 'Success');
          this.isLoading = false;
          this.cancel(true);
        }
      },
      error: (err) => {
        console.log('Error adding Procedure Code', err);
        this.createMessage('Error adding Procedure Code', 'Error');
        this.isLoading = false;
      }
    });
  }

}
